import { Component } from '@theme/component';

/**
 * A custom element that formats rte content for easier styling
 */
class RTEFormatter extends Component {
  connectedCallback() {
    super.connectedCallback();
    this.querySelectorAll('table').forEach(this.#formatTable);

    if (this.closest('.blog-post-content')) {
      this.querySelectorAll('img').forEach(this.#formatImage);
    }
  }

  /**
   * Formats a table for easier styling
   * @param {HTMLTableElement} table
   */
  #formatTable(table) {
    const wrapper = document.createElement('div');
    wrapper.classList.add('rte-table-wrapper');
    const parent = table.parentNode;
    if (parent) {
      parent.insertBefore(wrapper, table);
      wrapper.appendChild(table);
    }
  }

  /**
   * Moves an image's alt text into a visible span immediately after it.
   * Supports a small markdown-style syntax so editors can bold or link
   * part of the caption from within the alt field: **bold** and
   * [link text](url).
   * @param {HTMLImageElement} image
   */
  #formatImage(image) {
    const alt = image.alt.trim();
    if (!alt) return;

    const caption = document.createElement('span');
    caption.classList.add('rte-image-caption');
    caption.append(...RTEFormatter.#parseCaptionMarkdown(alt));
    image.insertAdjacentElement('afterend', caption);
  }

  /**
   * Turns **bold** and [text](url) segments into <strong>/<a> nodes,
   * leaving everything else as plain text.
   * @param {string} text
   * @returns {(Text | HTMLElement)[]}
   */
  static #parseCaptionMarkdown(text) {
    const pattern = /\*\*(.+?)\*\*|\[([^\]]+)\]\(([^)]+)\)/g;
    const nodes = [];
    let lastIndex = 0;
    let match;

    while ((match = pattern.exec(text)) !== null) {
      if (match.index > lastIndex) {
        nodes.push(document.createTextNode(text.slice(lastIndex, match.index)));
      }

      const [, boldText, linkText, linkHref] = match;

      if (boldText !== undefined) {
        const strong = document.createElement('strong');
        strong.textContent = boldText;
        nodes.push(strong);
      } else if (RTEFormatter.#isSafeHref(linkHref)) {
        const anchor = document.createElement('a');
        anchor.href = linkHref;
        anchor.textContent = linkText;
        nodes.push(anchor);
      } else {
        nodes.push(document.createTextNode(match[0]));
      }

      lastIndex = pattern.lastIndex;
    }

    if (lastIndex < text.length) {
      nodes.push(document.createTextNode(text.slice(lastIndex)));
    }

    return nodes;
  }

  /**
   * Only allow href schemes that can't execute script (blocks
   * `javascript:`, `data:`, etc.)
   * @param {string} href
   */
  static #isSafeHref(href) {
    return /^(https?:\/\/|mailto:|tel:|\/|#)/i.test(href.trim());
  }
}

if (!customElements.get('rte-formatter')) {
  customElements.define('rte-formatter', RTEFormatter);
}
